import React, { useEffect, useMemo, useRef, useState } from "react";
import { FeatherFileText } from "@subframe/core";
import { FullscreenDialog } from "../../../../ui/components/FullscreenDialog";
import { Button } from "../../../../ui/components/Button";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import DocumentViewer from "../../../../components/DocumentViewer";
import {
  fetchStatementStatusApi,
  fetchStatementReviewApi,
  approveStatementReviewApi,
  rejectStatementReviewApi,
  retryStatementExtractionApi,
  processStatementViaTextractApi,
  StatementReview,
  ReviewTransaction,
  ReviewMatch,
  MatchTier,
} from "../../data/api/statementReviewApi";

/**
 * StatementReviewDialog — the LIVE review-before-push gate for PDF bank
 * statements (visual design taken from the BankStatementReviewFlow demo).
 *
 * Upload has already happened when this opens. It polls extraction state
 * (Reducto deep extract, 1–4 minutes), then shows the summary + editable
 * transaction table beside the source PDF. Approve pushes the (possibly
 * edited) rows into the existing pipeline: dedup -> Zoho Books import ->
 * statements/transactions -> bank sync. Reject is terminal (statement is
 * marked rejected, nothing posted); the user can then explicitly hand the
 * statement to the legacy Textract flow, which posts automatically.
 */

type Stage =
  | "extracting"
  | "review"
  | "approving"
  | "rejecting"
  | "done"
  | "rejected"
  | "failed";

interface Row {
  uid: number;
  date: string;
  desc: string;
  dr: number;
  cr: number;
  isDuplicate: boolean;
  /** ZOUR-87 grade from the backend. null = nothing matched. */
  tier: MatchTier | null;
  similarity: number | null;
  match: ReviewMatch | null;
  /** The reviewer's own decision — this, not the tier, decides what is pushed. */
  importRow: boolean;
  /** Match signals, carried back on approve so the NEXT upload can use them. */
  refNo: string | null;
  balance: number | null;
}

/**
 * How each tier is presented. Ported from the DuplicateReviewDemo mock shown to
 * the team on 2026-09-01 — same vocabulary and same colours, now driven by real
 * grades from the backend instead of fixtures.
 */
const TIER_META: Record<
  MatchTier,
  { shortLabel: string; chip: string; explain: string; skipByDefault: boolean }
> = {
  exact: {
    shortLabel: "DUPLICATE",
    chip: "border-[#fecaca] bg-[#fef2f2] text-[#b91c1c]",
    skipByDefault: true,
    explain:
      "Identical date, narration and amount already imported for this bank account.",
  },
  probable: {
    shortLabel: "POSSIBLE",
    chip: "border-[#fde68a] bg-[#fffbeb] text-[#92400e]",
    skipByDefault: true,
    explain:
      "Same date and amount as an imported transaction; the narration differs slightly (usually extraction drift).",
  },
  same_da: {
    shortLabel: "SAME DATE & AMT",
    chip: "border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8]",
    skipByDefault: false,
    explain:
      "An imported transaction has the same date and amount but a clearly different narration. Usually a genuine separate transaction — kept by default.",
  },
  infile: {
    shortLabel: "REPEATED IN FILE",
    chip: "border-[#ddd6fe] bg-[#f5f3ff] text-[#5b21b6]",
    skipByDefault: true,
    explain:
      "An identical row appears earlier in this same statement — likely the same transaction extracted twice.",
  },
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  recordId: number;
  filename: string;
  bankLabel: string;
  onFinished?: (message: string) => void;
}

const POLL_MS = 8000;

const inr = (n: number) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const usd = (n: number) =>
  "$" +
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** "HDFC Bank ****4321" — where the approved rows are about to land. */
const bankAccountLabel = (review: StatementReview | null): string => {
  if (!review) return "";
  const account = review.bank_account;
  const name = account?.bank_name || review.bank_name || "Unlinked account";
  return account?.account_number_masked
    ? `${name} · ${account.account_number_masked}`
    : name;
};

export const StatementReviewDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  jobId,
  recordId,
  filename,
  bankLabel,
  onFinished,
}) => {
  const [stage, setStage] = useState<Stage>("extracting");
  const [review, setReview] = useState<StatementReview | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [approveInfo, setApproveInfo] = useState<{
    pushed: number;
    duplicates: number;
    refused: number;
    syncOk: boolean;
  } | null>(null);
  const [confirm, setConfirm] = useState<
    "approve" | "reject" | "textract" | null
  >(null);
  const [textractQueueing, setTextractQueueing] = useState(false);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const nextUid = useRef(1);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStage("extracting");
      setReview(null);
      setRows([]);
      setElapsed(0);
      setError(null);
      setApproveInfo(null);
      setConfirm(null);
      setExpanded({});
    }
  }, [open, jobId]);

  // Elapsed-time ticker while extracting
  useEffect(() => {
    if (!open || stage !== "extracting") return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [open, stage]);

  // Poll extraction state, then load the review payload
  useEffect(() => {
    if (!open || stage !== "extracting") return;
    let cancelled = false;

    const poll = async () => {
      try {
        const status = await fetchStatementStatusApi(jobId);
        if (cancelled) return;

        if (status.status === "awaiting_approval") {
          const data = await fetchStatementReviewApi(recordId);
          if (cancelled) return;
          setReview(data);
          setRows(
            data.transactions.map((t) => ({
              uid: nextUid.current++,
              date: t.date,
              desc: t.description,
              dr: t.debit_or_credit === "debit" ? t.amount : 0,
              cr: t.debit_or_credit === "credit" ? t.amount : 0,
              isDuplicate: t.is_duplicate === true,
              tier: t.tier ?? null,
              similarity: t.similarity ?? null,
              match: t.match ?? null,
              // The backend seeds the tick from the tier; an older backend
              // sends neither, which reads as "import everything" — exactly
              // what this screen did before ZOUR-87.
              importRow: t.import ?? true,
              refNo: t.reference_no ?? null,
              balance: t.balance ?? null,
            }))
          );
          setExpanded({});
          setStage("review");
          return;
        }

        if (
          status.queue_status === "failed" ||
          status.textract_status === "failed"
        ) {
          setError(
            status.error_message ||
              "Extraction failed. Retry below, or use the standard extraction instead."
          );
          setStage("failed");
        }
      } catch {
        // transient poll error — keep polling
      }
    };

    poll();
    const t = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [open, stage, jobId, recordId]);

  const derived = useMemo(() => {
    const opening = review?.summary?.opening_balance ?? null;
    let bal = opening ?? 0;
    let totalCr = 0;
    let totalDr = 0;
    let creditCount = 0;
    let debitCount = 0;
    const balances: Record<number, number | null> = {};
    rows.forEach((r) => {
      totalCr += r.cr || 0;
      totalDr += r.dr || 0;
      if ((r.cr || 0) > 0) creditCount += 1;
      if ((r.dr || 0) > 0) debitCount += 1;
      if (opening !== null) {
        bal += (r.cr || 0) - (r.dr || 0);
        balances[r.uid] = bal;
      } else {
        balances[r.uid] = null;
      }
    });
    return { balances, totalCr, totalDr, creditCount, debitCount };
  }, [rows, review]);

  const duplicateCount = useMemo(
    () => rows.filter((r) => r.isDuplicate).length,
    [rows]
  );

  /** What the reviewer has actually chosen to push. */
  const selected = useMemo(() => {
    const list = rows.filter((r) => r.importRow);
    return {
      count: list.length,
      dr: list.reduce((sum, r) => sum + (r.dr || 0), 0),
      cr: list.reduce((sum, r) => sum + (r.cr || 0), 0),
    };
  }, [rows]);

  const skippedCount = rows.length - selected.count;

  const flaggedRows = useMemo(() => rows.filter((r) => r.tier !== null), [rows]);
  const cleanRows = useMemo(() => rows.filter((r) => r.tier === null), [rows]);

  /** Rows a bulk link would actually change — the link hides when it is a no-op. */
  const bulkTargets = useMemo(
    () => ({
      skippableDuplicates: rows.filter(
        (r) =>
          r.importRow &&
          (r.tier === "exact" || r.tier === "probable" || r.tier === "infile")
      ).length,
      importableSameDa: rows.filter((r) => !r.importRow && r.tier === "same_da")
        .length,
    }),
    [rows]
  );

  const setImportWhere = (
    predicate: (row: Row) => boolean,
    value: boolean
  ) => {
    setRows((prev) =>
      prev.map((r) => (predicate(r) ? { ...r, importRow: value } : r))
    );
  };

  const toggleRow = (uid: number) =>
    setRows((prev) =>
      prev.map((r) => (r.uid === uid ? { ...r, importRow: !r.importRow } : r))
    );

  const toggleExpand = (uid: number) =>
    setExpanded((prev) => ({ ...prev, [uid]: !prev[uid] }));

  const invalidRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          !r.date.trim() ||
          !r.desc.trim() ||
          ((r.dr || 0) > 0 && (r.cr || 0) > 0) ||
          ((r.dr || 0) <= 0 && (r.cr || 0) <= 0)
      ),
    [rows]
  );

  const editRow = (uid: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  };

  /**
   * ALL rows go back, ticked and unticked alike: the unticked ones are the
   * audit trail of what the reviewer chose to leave out, and the backend
   * records them with their tier and the reason.
   *
   * `balance` and `reference_no` used to be dropped here (sent as null and not
   * sent at all). That quietly starved the duplicate matcher: rows imported
   * through this screen stored no match signals, so the NEXT upload had nothing
   * but the narration to compare against.
   */
  const toTransactions = (): ReviewTransaction[] =>
    rows.map((r) => ({
      transaction_id: "",
      date: r.date,
      debit_or_credit: (r.dr || 0) > 0 ? "debit" : "credit",
      amount: (r.dr || 0) > 0 ? r.dr : r.cr,
      payee: r.desc,
      description: r.desc,
      balance: r.balance,
      reference_no: r.refNo,
      import: r.importRow,
      tier: r.tier,
      similarity: r.similarity,
    }));

  const handleApprove = async () => {
    setConfirm(null);
    setStage("approving");
    setError(null);
    try {
      const result = await approveStatementReviewApi(recordId, toTransactions());
      setApproveInfo({
        pushed: result.processed_transaction_count,
        duplicates: result.duplicate_transaction_count,
        refused: result.refused_transaction_count ?? 0,
        syncOk: result.sync_status === "success",
      });
      setStage("done");
      onFinished?.(
        `Statement approved. ${result.processed_transaction_count} transactions pushed to Zoho Books`
      );
    } catch (e: any) {
      setError(e?.message || "Approval failed. Please try again.");
      setStage("review");
    }
  };

  const handleReject = async () => {
    setConfirm(null);
    setStage("rejecting");
    setError(null);
    try {
      await rejectStatementReviewApi(recordId);
      setStage("rejected");
      onFinished?.("Statement rejected. Nothing was posted to Zoho Books");
    } catch (e: any) {
      setError(e?.message || "Could not reject this extraction.");
      setStage("review");
    }
  };

  // Manual retry from the failed state. Extraction bills real money, so this
  // never happens automatically.
  const handleRetry = async () => {
    setError(null);
    try {
      await retryStatementExtractionApi(recordId);
      setElapsed(0);
      setStage("extracting");
    } catch (e: any) {
      setError(e?.message || "Could not re-queue the extraction.");
    }
  };

  // Explicit hand-off to the legacy Textract flow (extracts AND posts to
  // Zoho automatically, no review screen) — a conscious user choice from the
  // rejected or failed state, never a side effect of rejecting.
  const handleTextract = async () => {
    setConfirm(null);
    setError(null);
    setTextractQueueing(true);
    try {
      await processStatementViaTextractApi(recordId);
      onFinished?.(
        "Statement queued for the standard extraction flow. It will be processed and posted automatically"
      );
      close();
    } catch (e: any) {
      setError(e?.message || "Could not queue the standard extraction.");
    } finally {
      setTextractQueueing(false);
    }
  };

  const close = () => onOpenChange(false);

  return (
    <FullscreenDialog open={open} onOpenChange={onOpenChange}>
      <div className="flex h-full w-full flex-col bg-neutral-50">
        {/* Header */}
        <div className="flex w-full items-center gap-3 border-b border-solid border-neutral-border bg-default-background px-6 py-3">
          <IconWithBackground variant="success" icon={<FeatherFileText />} />
          <div className="flex flex-col">
            <span className="text-body-bold font-body-bold text-default-font">
              Review bank statement: {bankLabel}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              {filename} · review before posting to Zoho
            </span>
          </div>
          <div className="grow" />
          <Button variant="neutral-tertiary" onClick={close}>
            Close
          </Button>
        </div>

        {/* Body */}
        <div className="flex w-full grow flex-col overflow-hidden p-4">
          {stage === "extracting" && (
            <div className="mx-auto mt-10 flex w-full max-w-[560px] flex-col items-center gap-2 text-center">
              <div className="mb-2 h-10 w-10 animate-spin rounded-full border-[3px] border-neutral-200 border-t-brand-primary" />
              <span className="text-body-bold font-body-bold text-default-font">
                Extracting your statement…
              </span>
              <span className="text-body font-body text-subtext-color">
                Reading every transaction from the PDF. This usually takes 1
                to 4 minutes. You can close this and come back; the statement
                will wait for your review.
              </span>
              <span className="mt-2 text-caption font-caption text-subtext-color">
                Elapsed: {Math.floor(elapsed / 60)}:
                {String(elapsed % 60).padStart(2, "0")}
              </span>
            </div>
          )}

          {stage === "failed" && (
            <div className="mx-auto mt-10 flex w-full max-w-[560px] flex-col items-center gap-3 text-center">
              <span className="text-heading-3 font-heading-3 text-error-700">
                Extraction failed
              </span>
              <span className="text-body font-body text-subtext-color">{error}</span>
              <div className="mt-2 flex items-center gap-2">
                <Button variant="neutral-secondary" onClick={close}>
                  Close
                </Button>
                <Button onClick={handleRetry}>Retry extraction</Button>
              </div>
            </div>
          )}

          {(stage === "review" ||
            stage === "approving" ||
            stage === "rejecting") &&
            review && (
            <div className="flex min-h-0 w-full grow flex-col gap-3">
              {error && (
                <div className="rounded-md border border-solid border-error-300 bg-error-50 px-4 py-3 text-body font-body text-error-700">
                  {error}
                </div>
              )}

              <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)]">
                {/* LEFT: source PDF */}
                <div className="flex min-h-0 flex-col">
                  {review.pdf_url ? (
                    <DocumentViewer
                      pdfUrl={review.pdf_url as string}
                      fileName={filename}
                      className="h-full min-h-0 flex-1"
                      continuousScroll
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background text-body font-body text-subtext-color">
                      PDF preview unavailable
                    </div>
                  )}
                </div>

                {/* RIGHT: summary + read-only table. "Posting to" lives in
                    the footer beside Reject/Approve; extraction cost stays in
                    the DB (reducto_extractions), deliberately not shown. */}
                <div className="flex min-h-0 flex-col gap-3">
                  {/* Five cards. A sixth "Will be pushed" card was dropped
                      (ZOUR-87 addendum): on a re-upload where everything is a
                      duplicate it read "0 of 35 · 35 skipped", which lands as a
                      failure rather than as the correct outcome. The footer
                      button already carries the same count, next to the action
                      it describes. */}
                  <div className="grid flex-none grid-cols-3 gap-2.5 xl:grid-cols-5">
                    <Stat
                      label="Transactions"
                      value={String(rows.length)}
                      sub={`${derived.creditCount} credit · ${derived.debitCount} debit`}
                    />
                    <Stat
                      label="Total debit"
                      value={inr(derived.totalDr)}
                      valueClass="text-error-700"
                    />
                    <Stat
                      label="Total credit"
                      value={inr(derived.totalCr)}
                      valueClass="text-success-700"
                    />
                    <Stat
                      label="Opening balance"
                      value={
                        review.summary?.opening_balance !== null &&
                        review.summary?.opening_balance !== undefined
                          ? inr(review.summary.opening_balance)
                          : "—"
                      }
                    />
                    <Stat
                      label="Closing balance"
                      value={
                        review.summary?.closing_balance !== null &&
                        review.summary?.closing_balance !== undefined
                          ? inr(review.summary.closing_balance)
                          : "—"
                      }
                    />
                  </div>

                  {flaggedRows.length > 0 && (
                    <div className="flex flex-none flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-solid border-[#fde68a] bg-[#fffbeb] px-3.5 py-2 text-caption font-caption text-[#92400e]">
                      <span className="text-caption-bold font-caption-bold">
                        {flaggedRows.length} row
                        {flaggedRows.length === 1 ? "" : "s"} need your review
                      </span>
                      {(
                        ["exact", "probable", "same_da", "infile"] as MatchTier[]
                      ).map((tier) => {
                        const n = rows.filter((r) => r.tier === tier).length;
                        return n === 0 ? null : (
                          <span key={tier}>
                            {n} {TIER_META[tier].shortLabel.toLowerCase()}
                          </span>
                        );
                      })}
                      <div className="grow" />
                      {bulkTargets.skippableDuplicates > 0 && (
                        <button
                          className="underline hover:no-underline"
                          onClick={() =>
                            setImportWhere(
                              (r) =>
                                r.tier === "exact" ||
                                r.tier === "probable" ||
                                r.tier === "infile",
                              false
                            )
                          }
                        >
                          Skip all {bulkTargets.skippableDuplicates} duplicates
                        </button>
                      )}
                      {bulkTargets.importableSameDa > 0 && (
                        <button
                          className="underline hover:no-underline"
                          onClick={() =>
                            setImportWhere((r) => r.tier === "same_da", true)
                          }
                        >
                          Import all {bulkTargets.importableSameDa} same-date &amp;
                          amount
                        </button>
                      )}
                    </div>
                  )}

                  {/* Table */}
                  <div className="flex min-h-0 flex-1 flex-col rounded-md border border-solid border-neutral-border bg-default-background">
                    <div className="min-h-0 w-full flex-1 overflow-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-neutral-50">
                            <Th>Import</Th>
                            <Th>Duplicate check</Th>
                            <Th>Date</Th>
                            <Th>Narration</Th>
                            <Th right>Withdrawal</Th>
                            <Th right>Deposit</Th>
                            <Th right>Balance</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Flagged first: the rows that need a decision are
                              the reason this screen exists, and burying them
                              in a 64-row table is how they get rubber-stamped. */}
                          {[
                            {
                              key: "flagged",
                              title: "Needs your review",
                              list: flaggedRows,
                              cls: "bg-[#fffbeb] text-[#92400e]",
                            },
                            {
                              key: "clean",
                              title: "Ready to post",
                              list: cleanRows,
                              cls: "bg-success-50 text-success-700",
                            },
                          ].map((section) =>
                            section.list.length === 0 ? null : (
                              <React.Fragment key={section.key}>
                                <tr>
                                  <td
                                    colSpan={7}
                                    className={`border-t border-solid border-neutral-border px-3 py-1.5 text-caption-bold font-caption-bold uppercase ${section.cls}`}
                                  >
                                    {section.title} ({section.list.length})
                                  </td>
                                </tr>
                                {section.list.map((r) => {
                                  const invalid = invalidRows.includes(r);
                                  const meta = r.tier ? TIER_META[r.tier] : null;
                                  const isExpanded = expanded[r.uid] === true;
                                  return (
                                    <React.Fragment key={r.uid}>
                                      <tr
                                        className={`border-t border-solid border-neutral-border ${
                                          invalid
                                            ? "bg-error-50"
                                            : !r.importRow
                                            ? "bg-neutral-100 opacity-60"
                                            : "hover:bg-neutral-50"
                                        }`}
                                      >
                                        <td className="px-3 py-1 text-center align-middle">
                                          <input
                                            type="checkbox"
                                            checked={r.importRow}
                                            onChange={() => toggleRow(r.uid)}
                                            className="h-4 w-4 cursor-pointer accent-brand-600"
                                            title={
                                              r.importRow
                                                ? "Will be pushed to Zoho"
                                                : "Will be skipped"
                                            }
                                          />
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-1 align-middle">
                                          {meta && r.tier ? (
                                            <button
                                              onClick={() => toggleExpand(r.uid)}
                                              title={`${meta.explain} Click for details.`}
                                              className={`whitespace-nowrap rounded-full border border-solid px-1.5 py-px text-[10px] font-[700] leading-4 ${meta.chip}`}
                                            >
                                              {meta.shortLabel}
                                              {r.tier === "probable" &&
                                              r.similarity !== null
                                                ? ` · ${r.similarity}%`
                                                : ""}
                                              {isExpanded ? " ▴" : " ▾"}
                                            </button>
                                          ) : (
                                            <span className="whitespace-nowrap rounded-full border border-solid border-[#bbf7d0] bg-success-50 px-1.5 py-px text-[10px] font-[700] leading-4 text-success-700">
                                              READY TO POST
                                            </span>
                                          )}
                                        </td>
                                        {/* Read-only on purpose: the reviewer either
                                            approves what the PDF says or rejects the
                                            extraction — no hand edits. */}
                                        <td className="whitespace-nowrap px-3 py-1 align-middle text-body font-body text-default-font">
                                          {r.date}
                                        </td>
                                        <td className="min-w-[220px] max-w-[420px] px-3 py-1 align-middle">
                                          <div className="flex items-start gap-1.5">
                                            <span className="whitespace-pre-wrap break-words text-body font-body text-default-font">
                                              {r.desc}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-1 text-right align-middle text-body font-body text-default-font [font-variant-numeric:tabular-nums]">
                                          {r.dr > 0 ? inr(r.dr) : ""}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-1 text-right align-middle text-body font-body text-default-font [font-variant-numeric:tabular-nums]">
                                          {r.cr > 0 ? inr(r.cr) : ""}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-1 text-right align-middle text-caption font-caption text-subtext-color [font-variant-numeric:tabular-nums]">
                                          {derived.balances[r.uid] !== null
                                            ? inr(derived.balances[r.uid] as number)
                                            : "—"}
                                        </td>
                                      </tr>
                                      {isExpanded && r.tier && r.match && (
                                        <tr className="border-t border-solid border-neutral-border bg-neutral-50">
                                          <td />
                                          <td colSpan={6} className="px-3 py-2">
                                            <MatchDetails row={r} tier={r.tier} match={r.match} />
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </React.Fragment>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Actions — with the destination account right beside
                        the buttons, so the reviewer sees WHERE these rows
                        land at the moment they commit. */}
                    <div className="flex flex-none items-center gap-3 border-t border-solid border-neutral-border p-3">
                      <div className="flex min-w-0 flex-col">
                        <span className="text-caption-bold font-caption-bold uppercase text-subtext-color">
                          Posting to
                        </span>
                        <span className="truncate text-body-bold font-body-bold text-default-font">
                          {bankAccountLabel(review)}
                        </span>
                      </div>
                      {invalidRows.length > 0 && (
                        <span className="text-caption font-caption text-error-700">
                          {invalidRows.length} row
                          {invalidRows.length === 1 ? "" : "s"} need a date,
                          narration and exactly one amount
                        </span>
                      )}
                      <div className="grow" />
                      <Button
                        variant="destructive-secondary"
                        loading={stage === "rejecting"}
                        disabled={stage === "approving"}
                        onClick={() => setConfirm("reject")}
                      >
                        Reject
                      </Button>
                      <Button
                        loading={stage === "approving"}
                        disabled={
                          invalidRows.length > 0 ||
                          rows.length === 0 ||
                          selected.count === 0
                        }
                        onClick={() => setConfirm("approve")}
                      >
                        Approve &amp; push {selected.count} of {rows.length}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {stage === "rejected" && (
            <div className="mx-auto mt-10 flex w-full max-w-[560px] flex-col items-center gap-2 text-center">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Statement rejected
              </span>
              <span className="text-body font-body text-subtext-color">
                Nothing was posted to Zoho Books.
              </span>
              <div className="mt-5">
                <Button onClick={close}>Done</Button>
              </div>
            </div>
          )}

          {stage === "done" && approveInfo && (
            <div className="mx-auto mt-10 flex w-full max-w-[560px] flex-col items-center gap-2 text-center">
              <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full border border-solid border-[#bbf7d0] bg-success-50 text-success-600 text-heading-2 font-heading-2">
                ✓
              </div>
              <span className="text-heading-2 font-heading-2 text-default-font">
                Statement approved &amp; pushed
              </span>
              <span className="text-body font-body text-subtext-color">
                {approveInfo.pushed} transactions posted to Zoho Books
                {approveInfo.duplicates > 0
                  ? ` · ${approveInfo.duplicates} skipped (your selection)`
                  : ""}
                {approveInfo.refused > 0
                  ? ` · ${approveInfo.refused} refused as already imported by this statement`
                  : ""}
                {!approveInfo.syncOk &&
                  " · sync-back is pending and will retry automatically"}
              </span>
              <div className="mt-5">
                <Button onClick={close}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirm === "approve" && (
        <ConfirmBox
          title="Push to Zoho Books?"
          message={
            <>
              {selected.count} selected transaction
              {selected.count === 1 ? "" : "s"} ({inr(selected.dr)} debit /{" "}
              {inr(selected.cr)} credit) will be imported into{" "}
              {bankAccountLabel(review)}.
              {skippedCount > 0 && (
                <>
                  {" "}
                  <span className="text-body-bold font-body-bold text-default-font">
                    {skippedCount} row{skippedCount === 1 ? "" : "s"} will be
                    skipped
                  </span>{" "}
                  — your selection is recorded with the statement.
                </>
              )}
            </>
          }
          confirmLabel="Approve & push"
          onCancel={() => setConfirm(null)}
          onConfirm={handleApprove}
        />
      )}

      {confirm === "reject" && (
        <ConfirmBox
          title="Reject this extraction?"
          message="The statement will be marked as rejected. Nothing is posted to Zoho Books."
          confirmLabel="Reject"
          destructive
          onCancel={() => setConfirm(null)}
          onConfirm={handleReject}
        />
      )}

    </FullscreenDialog>
  );
};

/* ------------------------------ Small bits ------------------------------ */

/** Wrapping, auto-growing narration field — long narrations show fully. */
const NarrationInput: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(resize, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      placeholder="Description"
      onChange={(e) => onChange(e.target.value)}
      className="min-w-[180px] w-full resize-none overflow-hidden whitespace-pre-wrap break-words rounded-md border border-transparent bg-transparent px-2 py-1 text-body font-body text-default-font hover:border-neutral-border focus:border-brand-primary focus:bg-default-background focus:outline-none"
    />
  );
};

const DuplicateTag: React.FC = () => (
  <span
    title="An identical transaction (same date, narration and amount) was already imported for this bank account. This row will be skipped when pushing to Zoho."
    className="mt-1 whitespace-nowrap rounded-full border border-solid border-[#fde68a] bg-[#fffbeb] px-1.5 py-px text-[10px] font-[700] leading-4 text-[#92400e]"
  >
    DUPLICATE
  </span>
);

/**
 * Side-by-side comparison of the incoming row and whatever it matched, with the
 * signals that produced the grade. This is what turns "trust me, it's a
 * duplicate" into a decision the reviewer can actually make.
 */
const MatchDetails: React.FC<{
  row: Row;
  tier: MatchTier;
  match: ReviewMatch;
}> = ({ row, tier, match }) => {
  const meta = TIER_META[tier];
  const refMatches =
    !!row.refNo && !!match.reference_no && row.refNo === match.reference_no;
  const balancesKnown = row.balance !== null && match.balance !== null;

  return (
    <div className="flex max-w-[760px] flex-col gap-2 rounded-md border border-solid border-neutral-border bg-default-background p-3">
      <span className="text-caption-bold font-caption-bold text-default-font">
        {match.source === "this_file"
          ? "Matches another row in this statement"
          : match.imported_on
          ? `Matched a transaction imported on ${formatDay(match.imported_on)}`
          : "Matched a transaction already imported for this account"}
      </span>

      <div className="grid grid-cols-[70px_1fr] gap-x-3 gap-y-1">
        <span className="text-caption font-caption text-subtext-color">
          This file
        </span>
        <span className="break-words text-caption font-caption text-default-font">
          {row.date} · {row.desc} ·{" "}
          {row.dr > 0 ? `${inr(row.dr)} DR` : `${inr(row.cr)} CR`}
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {match.source === "this_file" ? "Earlier" : "Existing"}
        </span>
        <span className="break-words text-caption font-caption text-default-font">
          {match.date} · {match.description} · {inr(match.amount)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <SignalChip ok label="Date exact" />
        <SignalChip ok label="Amount exact" />
        {refMatches && (
          <SignalChip ok label={`Reference no. ${match.reference_no}`} />
        )}
        {row.similarity !== null && (
          <SignalChip
            ok={row.similarity >= 80}
            label={`Narration ${row.similarity}% similar`}
          />
        )}
        {balancesKnown && (
          <SignalChip
            ok={Math.abs((row.balance as number) - (match.balance as number)) < 0.01}
            label={
              Math.abs((row.balance as number) - (match.balance as number)) < 0.01
                ? "Running balance matches"
                : "Running balance differs"
            }
          />
        )}
      </div>

      <span className="text-caption font-caption text-subtext-color">
        {meta.explain}{" "}
        {meta.skipByDefault
          ? "Skipped by default — tick Import to push it anyway."
          : "Imported by default — untick Import to skip it."}
      </span>
    </div>
  );
};

const SignalChip: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
  <span
    className={`whitespace-nowrap rounded-full border border-solid px-2 py-px text-[10px] font-[600] leading-4 ${
      ok
        ? "border-[#bbf7d0] bg-success-50 text-success-700"
        : "border-neutral-border bg-neutral-50 text-subtext-color"
    }`}
  >
    {ok ? "✓" : "≈"} {label}
  </span>
);

/** `2026-08-18 10:00:00` -> `18 Aug 2026`; anything unparseable passes through. */
const formatDay = (value: string): string => {
  const parsed = new Date(value.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const Stat: React.FC<{
  label: string;
  value: string;
  valueClass?: string;
  sub?: string;
}> = ({ label, value, valueClass, sub }) => (
  <div className="flex flex-col gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-3.5 py-3">
    <span className="text-caption-bold font-caption-bold uppercase text-subtext-color">
      {label}
    </span>
    <span
      className={`text-heading-3 font-heading-3 [font-variant-numeric:tabular-nums] ${
        valueClass || "text-default-font"
      }`}
    >
      {value}
    </span>
    {sub && <span className="text-caption font-caption text-subtext-color">{sub}</span>}
  </div>
);

const Th: React.FC<{ children?: React.ReactNode; right?: boolean }> = ({
  children,
  right,
}) => (
  <th
    className={`border-b border-solid border-neutral-border px-3 py-2 text-caption-bold font-caption-bold uppercase text-subtext-color ${
      right ? "text-right" : "text-left"
    }`}
  >
    {children}
  </th>
);

const AmountInput: React.FC<{ value: number; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => (
  <input
    inputMode="decimal"
    value={value ? String(value) : ""}
    placeholder="0.00"
    onChange={(e) => onChange(e.target.value)}
    className="w-[110px] rounded-md border border-transparent bg-transparent px-2 py-1 text-right text-body font-body text-default-font [font-variant-numeric:tabular-nums] hover:border-neutral-border focus:border-brand-primary focus:bg-default-background focus:outline-none"
  />
);

const ConfirmBox: React.FC<{
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ title, message, confirmLabel, destructive, onConfirm, onCancel }) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-[#00000066] p-4"
    onClick={onCancel}
  >
    <div
      className="w-full max-w-[440px] overflow-hidden rounded-md border border-solid border-neutral-border bg-default-background shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col gap-2 p-5">
        <span className="text-heading-3 font-heading-3 text-default-font">{title}</span>
        <span className="text-body font-body text-subtext-color">{message}</span>
      </div>
      <div className="flex justify-end gap-2 border-t border-solid border-neutral-border p-3">
        <Button variant="neutral-secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant={destructive ? "destructive-primary" : "brand-primary"}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);

export default StatementReviewDialog;
